/**
 * TouchControls - Virtual joysticks for mobile gameplay
 * 
 * Layout:
 * - Left side: Movement joystick (strafe left/right, thrust up, brake down)
 * - Right side: Rotation joystick (rotate left/right, shoot while pressed)
 */
export class TouchControls {
    constructor(game) {
        this.game = game;
        this.touchActive = false;
        
        // Left joystick (movement)
        this.leftJoystickActive = false;
        this.leftJoystickId = null;
        this.leftJoystickCenter = { x: 0, y: 0 };
        this.leftJoystickOffset = { x: 0, y: 0 };
        
        // Right joystick (rotation + shoot)
        this.rightJoystickActive = false;
        this.rightJoystickId = null;
        this.rightJoystickCenter = { x: 0, y: 0 };
        this.rightJoystickOffset = { x: 0, y: 0 };
        
        // Touch zones
        this.leftZone = null;  // Left joystick zone (left 50%)
        this.rightZone = null; // Right joystick zone (right 50%)
        
        // Joystick visual elements
        this.leftJoystickBase = null;
        this.leftJoystickKnob = null;
        this.rightJoystickBase = null;
        this.rightJoystickKnob = null;
        
        this.joystickMaxRadius = 60; // pixels
        
        // Pinch-to-zoom state
        this.pinchDistance = 0;
        this.isPinching = false;
        
        this.init();
    }
    
    init() {
        // Create touch zones overlay
        this.createTouchZones();
        this.setupEventListeners();
    }
    
    createTouchZones() {
        // Create container
        const container = document.createElement('div');
        container.className = 'touch-controls-container';
        container.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: auto;
            z-index: 1000;
            display: none; /* Hidden by default, shown on mobile */
            touch-action: none;
            -webkit-touch-callout: none;
            -webkit-user-select: none;
            user-select: none;
        `;
        
        // Hidden by default - will be shown when game starts
        container.style.display = 'none';
        
        // Left zone (movement joystick)
        this.leftZone = document.createElement('div');
        this.leftZone.className = 'touch-zone left-zone';
        this.leftZone.style.cssText = `
            position: absolute;
            left: 0;
            top: 0;
            width: 50%;
            height: 100%;
            pointer-events: auto;
            touch-action: none;
            -webkit-touch-callout: none;
            -webkit-user-select: none;
            user-select: none;
        `;
        
        // Left joystick visual elements
        this.leftJoystickBase = document.createElement('div');
        this.leftJoystickBase.className = 'joystick-base left-joystick';
        this.leftJoystickBase.style.cssText = `
            position: absolute;
            width: ${this.joystickMaxRadius * 2}px;
            height: ${this.joystickMaxRadius * 2}px;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.1);
            border: 2px solid rgba(1, 189, 255, 0.5);
            display: none;
            pointer-events: none;
        `;
        
        this.leftJoystickKnob = document.createElement('div');
        this.leftJoystickKnob.className = 'joystick-knob';
        this.leftJoystickKnob.style.cssText = `
            position: absolute;
            width: 50px;
            height: 50px;
            border-radius: 50%;
            background: rgba(1, 189, 255, 0.8);
            border: 3px solid rgba(255, 255, 255, 0.9);
            box-shadow: 0 0 20px rgba(1, 189, 255, 0.6);
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            pointer-events: none;
        `;
        
        this.leftJoystickBase.appendChild(this.leftJoystickKnob);
        this.leftZone.appendChild(this.leftJoystickBase);
        
        // Right zone (rotation joystick)
        this.rightZone = document.createElement('div');
        this.rightZone.className = 'touch-zone right-zone';
        this.rightZone.style.cssText = `
            position: absolute;
            right: 0;
            top: 0;
            width: 50%;
            height: 100%;
            pointer-events: auto;
            touch-action: none;
            -webkit-touch-callout: none;
            -webkit-user-select: none;
            user-select: none;
        `;
        
        // Right joystick visual elements
        this.rightJoystickBase = document.createElement('div');
        this.rightJoystickBase.className = 'joystick-base right-joystick';
        this.rightJoystickBase.style.cssText = `
            position: absolute;
            width: ${this.joystickMaxRadius * 2}px;
            height: ${this.joystickMaxRadius * 2}px;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.1);
            border: 2px solid rgba(255, 145, 20, 0.5);
            display: none;
            pointer-events: none;
            z-index: 1001;
        `;
        
        this.rightJoystickKnob = document.createElement('div');
        this.rightJoystickKnob.className = 'joystick-knob';
        this.rightJoystickKnob.style.cssText = `
            position: absolute;
            width: 50px;
            height: 50px;
            border-radius: 50%;
            background: rgba(255, 145, 20, 0.8);
            border: 3px solid rgba(255, 255, 255, 0.9);
            box-shadow: 0 0 20px rgba(255, 145, 20, 0.6);
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            pointer-events: none;
        `;
        
        // Shooting indicator (pulsing circle when shooting)
        this.shootIndicator = document.createElement('div');
        this.shootIndicator.className = 'shoot-indicator';
        this.shootIndicator.style.cssText = `
            position: absolute;
            width: 60px;
            height: 60px;
            border-radius: 50%;
            background: rgba(255, 20, 20, 0.3);
            border: 2px solid rgba(255, 20, 20, 0.8);
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            pointer-events: none;
            display: none;
            animation: pulseShoot 0.3s infinite;
        `;
        
        // Add animation style if not already added
        if (!document.getElementById('touch-controls-styles')) {
            const style = document.createElement('style');
            style.id = 'touch-controls-styles';
            style.textContent = `
                @keyframes pulseShoot {
                    0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
                    50% { transform: translate(-50%, -50%) scale(1.2); opacity: 1; }
                }
                @keyframes rotateLeft {
                    0% { transform: translate(-50%, -50%) rotate(0deg); }
                    100% { transform: translate(-50%, -50%) rotate(-360deg); }
                }
                @keyframes rotateRight {
                    0% { transform: translate(-50%, -50%) rotate(0deg); }
                    100% { transform: translate(-50%, -50%) rotate(360deg); }
                }
            `;
            document.head.appendChild(style);
        }
        
        // Rotation indicator (arrow showing rotation direction)
        this.rotationIndicator = document.createElement('div');
        this.rotationIndicator.className = 'rotation-indicator';
        this.rotationIndicator.innerHTML = '↻';
        this.rotationIndicator.style.cssText = `
            position: absolute;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: rgba(255, 145, 20, 0.4);
            border: 2px solid rgba(255, 145, 20, 0.8);
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            pointer-events: none;
            display: none;
            font-size: 24px;
            color: rgba(255, 255, 255, 0.9);
            text-align: center;
            line-height: 40px;
            font-weight: bold;
        `;
        
        this.rightJoystickBase.appendChild(this.rightJoystickKnob);
        this.rightJoystickBase.appendChild(this.shootIndicator);
        this.rightJoystickBase.appendChild(this.rotationIndicator);
        this.rightZone.appendChild(this.rightJoystickBase);
        
        container.appendChild(this.leftZone);
        container.appendChild(this.rightZone);
        document.body.appendChild(container);
        
        this.container = container;
    }
    
    setupEventListeners() {
        // Left joystick controls (movement: strafe + thrust/brake)
        this.leftZone.addEventListener('touchstart', this.handleLeftJoystickStart.bind(this), { passive: false });
        this.leftZone.addEventListener('touchmove', this.handleLeftJoystickMove.bind(this), { passive: false });
        this.leftZone.addEventListener('touchend', this.handleLeftJoystickEnd.bind(this), { passive: false });
        this.leftZone.addEventListener('touchcancel', this.handleLeftJoystickEnd.bind(this), { passive: false });
        
        // Right joystick controls (rotation + shoot)
        this.rightZone.addEventListener('touchstart', this.handleRightJoystickStart.bind(this), { passive: false });
        this.rightZone.addEventListener('touchmove', this.handleRightJoystickMove.bind(this), { passive: false });
        this.rightZone.addEventListener('touchend', this.handleRightJoystickEnd.bind(this), { passive: false });
        this.rightZone.addEventListener('touchcancel', this.handleRightJoystickEnd.bind(this), { passive: false });
        
        // Pinch-to-zoom handlers
        // Note: touch-action: none on the zones should prevent most gestures
        // These handlers are for custom pinch-zoom functionality when NOT using joysticks
        document.addEventListener('touchstart', this.handlePinchStart.bind(this), { passive: false });
        document.addEventListener('touchmove', this.handlePinchMove.bind(this), { passive: false });
        document.addEventListener('touchend', this.handlePinchEnd.bind(this), { passive: false });
        document.addEventListener('touchcancel', this.handlePinchEnd.bind(this), { passive: false });
        
        // Handle window resize
        window.addEventListener('resize', this.handleResize.bind(this));
    }
    
    // Left joystick handlers (movement: strafe + thrust/brake)
    handleLeftJoystickStart(e) {
        e.preventDefault();
        
        // Find a touch in the left half of the screen
        let touch = null;
        for (let i = 0; i < e.touches.length; i++) {
            const t = e.touches[i];
            // Check if touch is in the left half of the screen
            if (t.clientX <= window.innerWidth / 2) {
                touch = t;
                break;
            }
        }
        
        // Fallback to first touch if none found in left zone
        if (!touch) {
            touch = e.touches[0];
        }
        
        this.leftJoystickId = touch.identifier;
        
        // Show joystick at touch position
        this.leftJoystickCenter.x = touch.clientX;
        this.leftJoystickCenter.y = touch.clientY;
        
        this.leftJoystickBase.style.left = `${this.leftJoystickCenter.x}px`;
        this.leftJoystickBase.style.top = `${this.leftJoystickCenter.y}px`;
        this.leftJoystickBase.style.transform = 'translate(-50%, -50%)';
        this.leftJoystickBase.style.display = 'block';
        
        this.leftJoystickActive = true;
        this.updateLeftJoystick(touch.clientX, touch.clientY);
    }
    
    handleLeftJoystickMove(e) {
        if (!this.leftJoystickActive) return;
        e.preventDefault();
        
        // Find our touch
        for (let touch of e.touches) {
            if (touch.identifier === this.leftJoystickId) {
                this.updateLeftJoystick(touch.clientX, touch.clientY);
                break;
            }
        }
    }
    
    handleLeftJoystickEnd(e) {
        e.preventDefault();
        this.leftJoystickActive = false;
        this.leftJoystickBase.style.display = 'none';
        
        // Reset movement inputs
        this.game.keyQ = false;
        this.game.keyE = false;
        this.game.keyUp = false;
        this.game.keyDown = false;
    }
    
    updateLeftJoystick(x, y) {
        // Calculate offset from center
        let dx = x - this.leftJoystickCenter.x;
        let dy = y - this.leftJoystickCenter.y;
        
        // Limit to max radius
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance > this.joystickMaxRadius) {
            dx = (dx / distance) * this.joystickMaxRadius;
            dy = (dy / distance) * this.joystickMaxRadius;
        }
        
        this.leftJoystickOffset.x = dx;
        this.leftJoystickOffset.y = dy;
        
        // Update visual position
        this.leftJoystickKnob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
        
        // Convert to game inputs
        this.updateLeftJoystickInputs();
    }
    
    updateLeftJoystickInputs() {
        const { x, y } = this.leftJoystickOffset;
        const threshold = 15; // Dead zone
        
        // Horizontal strafe (left/right)
        if (Math.abs(x) > threshold) {
            this.game.keyQ = x < 0; // Strafe left
            this.game.keyE = x > 0; // Strafe right
        } else {
            this.game.keyQ = false;
            this.game.keyE = false;
        }
        
        // Vertical movement (up = thrust, down = brake)
        if (y < -threshold) {
            this.game.keyUp = true;
            this.game.keyDown = false;
        } else if (y > threshold) {
            this.game.keyUp = false;
            this.game.keyDown = true;
        } else {
            this.game.keyUp = false;
            this.game.keyDown = false;
        }
    }
    
    // Right joystick handlers (rotation + shoot)
    handleRightJoystickStart(e) {
        e.preventDefault();
        
        // Find the newest touch (last in the array) or the first touch within the right zone
        let touch = null;
        for (let i = e.touches.length - 1; i >= 0; i--) {
            const t = e.touches[i];
            // Check if touch is in the right half of the screen
            if (t.clientX > window.innerWidth / 2) {
                touch = t;
                break;
            }
        }
        
        // Fallback to last touch if none found in right zone
        if (!touch) {
            touch = e.touches[e.touches.length - 1];
        }
        
        this.rightJoystickId = touch.identifier;
        
        // Get the rightZone's position relative to viewport
        const rightZoneRect = this.rightZone.getBoundingClientRect();
        
        // Calculate position relative to rightZone (not viewport)
        // Since rightZone is positioned absolutely within container, we need relative coordinates
        const relativeX = touch.clientX - rightZoneRect.left;
        const relativeY = touch.clientY - rightZoneRect.top;
        
        // Store absolute position for calculations
        this.rightJoystickCenter.x = touch.clientX;
        this.rightJoystickCenter.y = touch.clientY;
        
        // Set position relative to rightZone
        this.rightJoystickBase.style.left = `${relativeX}px`;
        this.rightJoystickBase.style.top = `${relativeY}px`;
        this.rightJoystickBase.style.transform = 'translate(-50%, -50%)';
        this.rightJoystickBase.style.display = 'block';
        
        // Show shooting indicator immediately
        if (this.shootIndicator) {
            this.shootIndicator.style.display = 'block';
        }
        
        this.rightJoystickActive = true;
        
        // Start shooting immediately when right joystick is pressed
        this.game.keySpace = true;
        
        this.updateRightJoystick(touch.clientX, touch.clientY);
    }
    
    handleRightJoystickMove(e) {
        if (!this.rightJoystickActive) return;
        e.preventDefault();
        
        // Find our touch
        for (let touch of e.touches) {
            if (touch.identifier === this.rightJoystickId) {
                this.updateRightJoystick(touch.clientX, touch.clientY);
                break;
            }
        }
    }
    
    handleRightJoystickEnd(e) {
        e.preventDefault();
        this.rightJoystickActive = false;
        this.rightJoystickBase.style.display = 'none';
        
        // Hide visual indicators
        if (this.shootIndicator) {
            this.shootIndicator.style.display = 'none';
        }
        if (this.rotationIndicator) {
            this.rotationIndicator.style.display = 'none';
        }
        
        // Reset rotation and shooting inputs
        this.game.keyLeft = false;
        this.game.keyRight = false;
        this.game.keySpace = false;
        
        // Reset rotation intensity (mobile)
        this.game.rotationIntensity = 0;
        this.game.rotationDirection = 0;
    }
    
    updateRightJoystick(x, y) {
        // HORIZONTAL SLIDER MODE: Only track horizontal movement for rotation
        // Vertical movement is ignored for cleaner control
        let dx = x - this.rightJoystickCenter.x;
        
        // Limit horizontal movement to max radius
        if (Math.abs(dx) > this.joystickMaxRadius) {
            dx = (dx > 0 ? 1 : -1) * this.joystickMaxRadius;
        }
        
        this.rightJoystickOffset.x = dx;
        this.rightJoystickOffset.y = 0; // Always 0 for horizontal slider
        
        // Update visual position - knob moves only horizontally
        this.rightJoystickKnob.style.transform = `translate(calc(-50% + ${dx}px), -50%)`;
        
        // Convert to game inputs with intensity
        this.updateRightJoystickInputs();
    }
    
    updateRightJoystickInputs() {
        const { x } = this.rightJoystickOffset;
        const deadZone = 10; // Small dead zone at center
        
        // Show shooting indicator (always on when right joystick is active)
        if (this.shootIndicator) {
            this.shootIndicator.style.display = 'block';
        }
        
        // Calculate rotation intensity (0.0 to 1.0) based on horizontal distance
        // Dead zone handling: within dead zone = 0 intensity
        let rotationIntensity = 0;
        let rotationDirection = 0; // 1 = rotate left, 0 = none, -1 = rotate right
        
        if (Math.abs(x) > deadZone) {
            // Calculate intensity as percentage of max radius
            const distanceBeyondDeadZone = Math.abs(x) - deadZone;
            const maxDistance = this.joystickMaxRadius - deadZone;
            rotationIntensity = Math.min(distanceBeyondDeadZone / maxDistance, 1.0);
            
            // Determine direction (inverted to match keyboard controls)
            // Left slide (x < 0) = rotate left = increase angle = direction 1
            // Right slide (x > 0) = rotate right = decrease angle = direction -1
            rotationDirection = x < 0 ? 1 : -1;
            
            // Update visual indicator
            if (this.rotationIndicator) {
                this.rotationIndicator.style.display = 'block';
                // rotationDirection: 1 = left rotation, -1 = right rotation
                this.rotationIndicator.style.animation = rotationDirection > 0 ? 
                    'rotateLeft 1s linear infinite' : 'rotateRight 1s linear infinite';
                this.rotationIndicator.innerHTML = rotationDirection > 0 ? '↺' : '↻';
                
                // Show intensity as percentage
                const intensityPercent = Math.round(rotationIntensity * 100);
                this.rotationIndicator.innerHTML = rotationDirection > 0 ? 
                    `↺ ${intensityPercent}%` : `↻ ${intensityPercent}%`;
            }
        } else {
            // In dead zone - no rotation
            if (this.rotationIndicator) {
                this.rotationIndicator.style.display = 'none';
            }
        }
        
        // Set game rotation intensity (NEW: variable speed rotation)
        this.game.rotationIntensity = rotationIntensity;
        this.game.rotationDirection = rotationDirection;
        
        // DO NOT set keyLeft/keyRight on mobile - they override the intensity!
        // Mobile uses intensity, desktop uses keyboard keys
        // The game will use intensity if it's > 0, otherwise fall back to keys
        this.game.keyLeft = false;
        this.game.keyRight = false;
        
        // Keep shooting while right joystick is active
        this.game.keySpace = true;
    }
    
    
    // Pinch-to-zoom handlers
    handlePinchStart(e) {
        // With touch-action: none on joystick zones, gestures on those zones are already blocked
        // This handler only processes pinches that occur outside the joystick zones
        if (e.touches.length >= 2) {
            // Check if any touch is on a joystick zone
            let touchOnJoystick = false;
            for (let i = 0; i < e.touches.length; i++) {
                const touch = e.touches[i];
                const element = document.elementFromPoint(touch.clientX, touch.clientY);
                if (element && (
                    element.closest('.left-zone') || 
                    element.closest('.right-zone') ||
                    element.closest('.touch-controls-container')
                )) {
                    touchOnJoystick = true;
                    break;
                }
            }
            
            // Only enable pinch zoom if no touches are on joystick zones
            if (!touchOnJoystick && e.touches.length === 2) {
                this.isPinching = true;
                this.pinchDistance = this.getTouchDistance(e.touches[0], e.touches[1]);
            } else {
                // Prevent browser zoom when joysticks might be involved
                e.preventDefault();
            }
        }
    }
    
    handlePinchMove(e) {
        // If either joystick is active, prevent any zoom gestures
        if (this.leftJoystickActive || this.rightJoystickActive) {
            e.preventDefault();
            return;
        }
        
        if (this.isPinching && e.touches.length === 2) {
            e.preventDefault();
            const newDistance = this.getTouchDistance(e.touches[0], e.touches[1]);
            const delta = newDistance - this.pinchDistance;
            
            // Update zoom (negative delta = pinch in = zoom out)
            if (this.game && this.game.updateCameraZoom) {
                const zoomChange = -delta * 0.05; // Sensitivity factor
                this.game.updateCameraZoom(this.game.currentZoom + zoomChange);
            }
            
            this.pinchDistance = newDistance;
        }
    }
    
    handlePinchEnd(e) {
        if (e.touches.length < 2) {
            this.isPinching = false;
        }
    }
    
    getTouchDistance(touch1, touch2) {
        const dx = touch2.clientX - touch1.clientX;
        const dy = touch2.clientY - touch1.clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }
    
    isMobile() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
               window.innerWidth < 768;
    }
    
    handleResize() {
        // Only update visibility if game is started
        // Visibility is controlled by show()/hide() methods
    }
    
    // Show touch controls (called when game starts)
    show() {
        if (this.container && this.isMobile()) {
            this.container.style.display = 'block';
        }
    }
    
    // Hide touch controls (called when game ends or on menu screen)
    hide() {
        if (this.container) {
            this.container.style.display = 'none';
        }
        // Also reset any active inputs
        this.leftJoystickActive = false;
        this.rightJoystickActive = false;
        if (this.leftJoystickBase) this.leftJoystickBase.style.display = 'none';
        if (this.rightJoystickBase) this.rightJoystickBase.style.display = 'none';
        if (this.game) {
            this.game.keyLeft = false;
            this.game.keyRight = false;
            this.game.keyUp = false;
            this.game.keyDown = false;
            this.game.keySpace = false;
            this.game.keyQ = false;
            this.game.keyE = false;
        }
    }
    
    destroy() {
        // Remove event listeners
        if (this.leftZone) {
            this.leftZone.removeEventListener('touchstart', this.handleLeftJoystickStart);
            this.leftZone.removeEventListener('touchmove', this.handleLeftJoystickMove);
            this.leftZone.removeEventListener('touchend', this.handleLeftJoystickEnd);
            this.leftZone.removeEventListener('touchcancel', this.handleLeftJoystickEnd);
        }
        
        if (this.rightZone) {
            this.rightZone.removeEventListener('touchstart', this.handleRightJoystickStart);
            this.rightZone.removeEventListener('touchmove', this.handleRightJoystickMove);
            this.rightZone.removeEventListener('touchend', this.handleRightJoystickEnd);
            this.rightZone.removeEventListener('touchcancel', this.handleRightJoystickEnd);
        }
        
        document.removeEventListener('touchstart', this.handlePinchStart);
        document.removeEventListener('touchmove', this.handlePinchMove);
        document.removeEventListener('touchend', this.handlePinchEnd);
        document.removeEventListener('touchcancel', this.handlePinchEnd);
        
        window.removeEventListener('resize', this.handleResize);
        
        // Remove DOM elements
        if (this.container && this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
    }
}

